import { TokenList } from '@uniswap/token-lists'
import schema from '@uniswap/token-lists/src/tokenlist.schema.json'
import Ajv from 'ajv'
import contenthashToUri from './contenthashToUri'
import { parseENSAddress } from './parseENSAddress'
import uriToHttp from './uriToHttp'

// Uniswap schema 默认 name/symbol 正则过严（不含希腊字母等 Unicode），会导致 BTCΞ 这类 token 整表校验失败
const relaxedSchema = JSON.parse(JSON.stringify(schema))
const tokenInfo = relaxedSchema.definitions?.TokenInfo?.properties
if (tokenInfo?.name) {
  // 允许 Unicode 可见字符（含 Ξ、中文等），仍限制长度
  tokenInfo.name.pattern = '^[^\u0000-\u001F\u007F]+$'
}
if (tokenInfo?.symbol) {
  tokenInfo.symbol.pattern = '^[^\u0000-\u001F\u007F]+$'
}

const tokenListValidator = new Ajv({ allErrors: true }).compile(relaxedSchema)

/**
 * Contains the logic for resolving a list URL to a validated token list
 * @param listUrl list url
 * @param resolveENSContentHash resolves an ens name to a contenthash
 */
export default async function getTokenList(
  listUrl: string,
  resolveENSContentHash: (ensName: string) => Promise<string>
): Promise<TokenList> {
  const parsedENS = parseENSAddress(listUrl)
  let urls: string[]
  if (parsedENS) {
    let contentHashUri
    try {
      contentHashUri = await resolveENSContentHash(parsedENS.ensName)
    } catch (error) {
      console.debug(`Failed to resolve ENS name: ${parsedENS.ensName}`, error)
      throw new Error(`Failed to resolve ENS name: ${parsedENS.ensName}`)
    }
    let translatedUri
    try {
      translatedUri = contenthashToUri(contentHashUri)
    } catch (error) {
      console.debug('Failed to translate contenthash to URI', contentHashUri)
      throw new Error(`Failed to translate contenthash to URI: ${contentHashUri}`)
    }
    urls = uriToHttp(`${translatedUri}${parsedENS.ensPath ?? ''}`)
  } else {
    urls = uriToHttp(listUrl)
  }
  for (let i = 0; i < urls.length; i++) {
    const url = urls[i]
    const isLast = i === urls.length - 1
    console.debug(`getTokenList: Trying to fetch from ${url} (attempt ${i + 1}/${urls.length})`)
    let response
    try {
      response = await fetch(url)
    } catch (error) {
      console.debug('Failed to fetch list', listUrl, error)
      if (isLast) throw new Error(`Failed to download list ${listUrl}`)
      continue
    }

    if (!response.ok) {
      console.debug(`getTokenList: Response not ok for ${url}, status: ${response.status}`)
      if (isLast) throw new Error(`Failed to download list ${listUrl}`)
      continue
    }

    const json = await response.json()
    console.debug(`getTokenList: Successfully fetched JSON from ${url}`, json)
    if (!tokenListValidator(json)) {
      const validationErrors: string =
        tokenListValidator.errors?.reduce<string>((memo, error) => {
          const add = `${error.dataPath} ${error.message ?? ''}`
          return memo.length > 0 ? `${memo}; ${add}` : `${add}`
        }, '') ?? 'unknown error'
      console.error(`getTokenList: Token list validation failed for ${url}`, validationErrors)
      throw new Error(`Token list failed validation: ${validationErrors}`)
    }
    console.debug(`getTokenList: Token list validation passed for ${url}`)
    return json
  }
  throw new Error('Unrecognized list URL protocol.')
}
